// New with multiple apps + intents + custom intents + domain re-hydration
import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, CardBody, Button, Input, Label, FormGroup, FormFeedback } from 'reactstrap';
import { connect } from 'react-redux';
import WidgetsSectionTitle from '../../../WidgetsSectionTitle/WidgetSectionTitle';
import { toast } from 'react-toastify';

// Chatbot config CRUD
import {
  fetchChatbotConfig,
  createChatbotConfig,
  updateChatbotConfig,
  deleteChatbotConfig,
} from '../../../Redux/Actions/AI/aichatbot';

// Podio lookups
import {
  fetchPodioAccounts,
  fetchPodioOrgs,
  fetchPodioSpaces,
  fetchPodioApps,
} from '../../../Redux/Actions/AI/podiolookup';

// Intents by domain
import { fetchIntentsByDomain } from '../../../Redux/Actions/AI/Intents';

const EMPTY_ROW = {
  accountId: '',
  orgId: '',
  spaceId: '',
  domain: '',
  appBundles: [] // [{ intent, isCustom?, customType?, customText?, mappings:[{purpose, appId}] }]
};

// UI industry→domain mapping
const INDUSTRY_OPTIONS = [
  { label: 'Real Estate',   domain: 'REAL_ESTATE' },
  { label: 'Health Care',   domain: 'HEALTH_CARE' },
  { label: 'Product',       domain: 'PRODUCT' },
  { label: 'E-commerce',    domain: 'E_COMMERCE' },
  { label: 'Construction',  domain: 'CONSTRUCTION' },
  { label: 'Marine',        domain: 'MARINE' },
];

// Helpers to format/parse intent labels
const formatIntentLabel = (key, description) =>
  `${String(key || '').trim()}: ${String(description || '').trim()}`.trim();

const extractIntentKey = (storedIntent) =>
  (storedIntent || '').split(':')[0].trim();

const isNonEmpty = (v) => typeof v === 'string' && v.trim().length > 0;

const ChatbotConfig = ({
  // podio lookup
  podioLoading,
  accounts,
  orgsByAccount,
  spacesByOrg,
  appsBySpace,

  // intents
  intentsLoading,
  intentsByDomain,

  // actions
  fetchPodioAccounts,
  fetchPodioOrgs,
  fetchPodioSpaces,
  fetchPodioApps,
  fetchIntentsByDomain,

  // chatbot config
  fetchChatbotConfig,
  createChatbotConfig,
  updateChatbotConfig,
  deleteChatbotConfig,
}) => {
  const [row, setRow] = useState({ ...EMPTY_ROW });
  const [configId, setConfigId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [savedPodio, setSavedPodio] = useState([]); // ← cache all selections from DB

  const getOrgs   = (accountId) => orgsByAccount?.[accountId] || [];
  const getSpaces = (orgId)     => spacesByOrg?.[orgId] || [];
  const getApps   = (spaceId)   => appsBySpace?.[spaceId] || [];

  // Shape helpers
  const toClientBundles = (appArray) => {
    return (Array.isArray(appArray) ? appArray : []).map((b) => ({
      intent: b.intent || '',
      isCustom: false,
      customType: '',
      customText: '',
      mappings: Array.isArray(b.mappings)
        ? b.mappings.map(m => ({ purpose: m.purpose || '', appId: m.appId || '' }))
        : [{ purpose: '', appId: '' }]
    }));
  };

  // --- Init: accounts + existing config ---
  useEffect(() => {
    fetchPodioAccounts();
    (async () => {
      const res = await fetchChatbotConfig();
      if (res?._id && Array.isArray(res.podio) && res.podio.length > 0) {
        setConfigId(res._id);
        setSavedPodio(res.podio); // cache all selections

        // choose first selection for initial hydration
        const first = res.podio[0];
        const initialRow = {
          accountId: first.accountId || '',
          orgId: first.organization || '',
          spaceId: first.workspace || '',
          domain: first.domain || '',
          appBundles: toClientBundles(first.app)
        };
        setRow(initialRow);

        // Preload cascades
        if (first.accountId) await fetchPodioOrgs(first.accountId);
        if (first.accountId && first.organization) await fetchPodioSpaces(first.accountId, first.organization);
        if (first.accountId && first.workspace) await fetchPodioApps(first.accountId, first.workspace);

        // Preload intents for domain if present
        if (first.domain) await fetchIntentsByDomain(first.domain);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After intents load, 1) upgrade legacy key-only intents to "key: desc",
  // 2) determine which bundles are "custom" vs "dropdown" by checking the key in the loaded set.
  useEffect(() => {
  if (!row.domain) return;
  const domainData = intentsByDomain?.[row.domain];
  const items = domainData?.items || [];
  if (!items.length) return;

  const descByKey = new Map(items.map(it => [it.key, it.description || '']));
  const knownKeys = new Set(items.map(it => it.key));

  let changed = false;
  const upgraded = row.appBundles.map(b => {
    if (!b?.intent) return b;

    const hasColon = b.intent.includes(':');
    let key = extractIntentKey(b.intent);

    if (!hasColon && knownKeys.has(key)) {
      const full = formatIntentLabel(key, descByKey.get(key));
      changed = true;
      return { ...b, intent: full, isCustom: false, customType: '', customText: '' };
    }
    if (hasColon && !knownKeys.has(key)) {
      const text = (b.intent.split(':').slice(1).join(':') || '').trim();
      if (b.isCustom && b.customType === key && b.customText === text) return b;
      changed = true;
      return { ...b, isCustom: true, customType: key, customText: text };
    }
    if (!hasColon && !knownKeys.has(key)) {
      changed = true;
      return { ...b, isCustom: true, customType: key, customText: '' };
    }
    return { ...b, isCustom: false, customType: '', customText: '' };
  });

  if (changed) setRow(r => ({ ...r, appBundles: upgraded }));
// ⬇️ add row.appBundles here
}, [row.domain, intentsByDomain, row.appBundles]);

  
  // useEffect(() => {
  //   if (!row.domain) return;
  //   const domainData = intentsByDomain?.[row.domain];
  //   const items = domainData?.items || [];
  //   if (!items.length) return;

  //   const descByKey = new Map(items.map(it => [it.key, it.description || '']));
  //   const knownKeys = new Set(items.map(it => it.key));

  //   let changed = false;
  //   const upgraded = row.appBundles.map(b => {
  //     if (!b?.intent) return b;

  //     const hasColon = b.intent.includes(':');
  //     let key = extractIntentKey(b.intent);

  //     // If legacy (no colon) and known key → expand with description
  //     if (!hasColon && knownKeys.has(key)) {
  //       const full = formatIntentLabel(key, descByKey.get(key));
  //       changed = true;
  //       return { ...b, intent: full, isCustom: false, customType: '', customText: '' };
  //     }

  //     // If colon exists but key not in known set → treat as custom
  //     if (hasColon && !knownKeys.has(key)) {
  //       const text = (b.intent.split(':').slice(1).join(':') || '').trim();
  //       if (b.isCustom && b.customType === key && b.customText === text) return b;
  //       changed = true;
  //       return { ...b, isCustom: true, customType: key, customText: text };
  //     }

  //     // If no colon and unknown key → custom
  //     if (!hasColon && !knownKeys.has(key)) {
  //       changed = true;
  //       return { ...b, isCustom: true, customType: key, customText: '' };
  //     }

  //     // Known dropdown intent
  //     return { ...b, isCustom: false, customType: '', customText: '' };
  //   });

  //   if (changed) setRow(r => ({ ...r, appBundles: upgraded }));
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [row.domain, intentsByDomain]);

  // --- Select lists ---
  const accountOptions = accounts;
  const orgOptions   = row.accountId ? getOrgs(row.accountId) : [];
  const spaceOptions = row.orgId ? getSpaces(row.orgId) : [];
  const appOptions   = row.spaceId ? getApps(row.spaceId) : [];

  const availableIntents = useMemo(() => {
    const domainData = intentsByDomain?.[row.domain];
    const items = domainData?.items || [];
    return items; // [{key,description}]
  }, [intentsByDomain, row.domain]);

  // --- Cascading handlers ---
  const onAccountChange = async (newAccountId) => {
    setRow({ ...EMPTY_ROW, accountId: newAccountId });
    if (newAccountId) await fetchPodioOrgs(newAccountId);
  };

  const onOrgChange = async (accountId, newOrgId) => {
    setRow((r) => ({ ...r, orgId: newOrgId, spaceId: '', domain: '', appBundles: [] }));
    if (accountId && newOrgId) await fetchPodioSpaces(accountId, newOrgId);
  };

  const onSpaceChange = async (accountId, newSpaceId) => {
    setRow((r) => ({ ...r, spaceId: newSpaceId, domain: '', appBundles: [] }));
    if (accountId && newSpaceId) await fetchPodioApps(accountId, newSpaceId);
  };

  // *** KEY FIX: when domain changes, try to re-hydrate from savedPodio ***
  const onIndustryChange = async (domain) => {
    // optimistic set domain + clear bundles first
    setRow((r) => ({ ...r, domain, appBundles: [] }));
    if (!domain) return;

    // Make sure intents are available (UI labels, custom detection)
    await fetchIntentsByDomain(domain);

    // Try to find a saved selection for SAME account/org/space + this domain
const match = (savedPodio || []).find(p =>
  String(p.accountId)   === String(row.accountId) &&
  String(p.organization)=== String(row.orgId) &&
  String(p.workspace)   === String(row.spaceId) &&
  String(p.domain)      === String(domain)
);

if (match && Array.isArray(match.app)) {
  const rawBundles = toClientBundles(match.app);
  const bundles = decorateBundlesWithMode(rawBundles, domain, intentsByDomain);
  setRow((r) => ({ ...r, domain, appBundles: bundles }));
} else {
  setRow((r) => ({ ...r, domain, appBundles: [] }));
}

  };

  // --- Intent helpers ---
  const currentBundleKey = (bundle) => {
    return bundle.isCustom ? (bundle.customType || '') : extractIntentKey(bundle.intent);
  };

  const selectedIntentKeys = new Set(
    row.appBundles.map(b => currentBundleKey(b)).filter(Boolean)
  );

  const intentAlreadySelected = (key, allowKey) => {
    if (allowKey && key === allowKey) return false;
    return selectedIntentKeys.has(key);
  };

  const addIntent = () => {
    if (!row.domain) {
      toast.error("Select an industry first.");
      return;
    }
    setRow((r) => ({
      ...r,
      appBundles: [
        ...r.appBundles,
        { intent: '', isCustom: false, customType: '', customText: '', mappings: [{ purpose: '', appId: '' }] }
      ],
    }));
  };

  const addCustomIntent = () => {
    if (!row.domain) {
      toast.error("Select an industry first.");
      return;
    }
    setRow((r) => ({
      ...r,
      appBundles: [
        ...r.appBundles,
        { intent: '', isCustom: true, customType: '', customText: '', mappings: [{ purpose: '', appId: '' }] }
      ],
    }));
  };

  const removeIntent = (idx) => {
    setRow((r) => {
      const copy = [...r.appBundles];
      copy.splice(idx, 1);
      return { ...r, appBundles: copy };
    });
  };

  const toggleIntentMode = (idx, toCustom) => {
    setRow((r) => {
      const copy = [...r.appBundles];
      const bundle = { ...copy[idx] };
      if (toCustom) {
        bundle.isCustom = true;
        const k = extractIntentKey(bundle.intent);
        const t = (bundle.intent.split(':').slice(1).join(':') || '').trim();
        bundle.customType = k || '';
        bundle.customText = t || '';
        bundle.intent = '';
      } else {
        bundle.isCustom = false;
        bundle.customType = '';
        bundle.customText = '';
      }
      copy[idx] = bundle;
      return { ...r, appBundles: copy };
    });
  };

  const setIntentKey = (idx, intentKey, description) => {
    const fullLabel = formatIntentLabel(intentKey, description);
    setRow((r) => {
      const copy = [...r.appBundles];
      copy[idx] = { ...copy[idx], intent: fullLabel };
      return { ...r, appBundles: copy };
    });
  };

  // Mapping rows per intent
  const addMapping = (intentIdx) => {
    setRow((r) => {
      const bundles = [...r.appBundles];
      const item = bundles[intentIdx];
      item.mappings = [...item.mappings, { purpose: '', appId: '' }];
      return { ...r, appBundles: bundles };
    });
  };

  const removeMapping = (intentIdx, mapIdx) => {
    setRow((r) => {
      const bundles = [...r.appBundles];
      const item = bundles[intentIdx];
      const maps = [...item.mappings];
      maps.splice(mapIdx, 1);
      item.mappings = maps.length ? maps : [{ purpose: '', appId: '' }];
      return { ...r, appBundles: bundles };
    });
  };

  const setMappingField = (intentIdx, mapIdx, field, value) => {
    setRow((r) => {
      const bundles = [...r.appBundles];
      const item = bundles[intentIdx];
      const maps = [...item.mappings];
      maps[mapIdx] = { ...maps[mapIdx], [field]: value };
      item.mappings = maps;
      return { ...r, appBundles: bundles };
    });
  };

  const setCustomField = (idx, field, value) => {
    setRow((r) => {
      const copy = [...r.appBundles];
      copy[idx] = { ...copy[idx], [field]: value };
      return { ...r, appBundles: copy };
    });
  };

  // Disable app used within the same intent
  const appDisabledInIntent = (intentIdx, appId) => {
    const bundle = row.appBundles[intentIdx];
    return bundle.mappings.some((m) => m.appId === appId);
  };

  // --- Validation ---
  const baseSelectorsComplete = row.accountId && row.orgId && row.spaceId;
  const industrySelected = !!row.domain;

  // Build per-field invalid flags for red borders
  const invalidFlags = useMemo(() => {
    const flags = {
      accountId: !isNonEmpty(row.accountId),
      orgId: !isNonEmpty(row.orgId),
      spaceId: !isNonEmpty(row.spaceId),
      domain: !isNonEmpty(row.domain),
      bundles: []
    };

    // Intent key uniqueness check
    const seenIntentKeys = new Set();

    row.appBundles.forEach((b) => {
      const key = b.isCustom ? (b.customType || '') : extractIntentKey(b.intent);
      const bundleFlags = {
        isCustom: !!b.isCustom,
        intentSelect: false,
        customType: false,
        customText: false,
        mappings: []
      };

      if (b.isCustom) {
        if (!isNonEmpty(b.customType)) bundleFlags.customType = true;
        if (!isNonEmpty(b.customText)) bundleFlags.customText = true;
      } else {
        if (!isNonEmpty(b.intent)) bundleFlags.intentSelect = true;
      }

      // Duplicate intent key?
      if (isNonEmpty(key)) {
        if (seenIntentKeys.has(key)) {
          bundleFlags.intentSelect = bundleFlags.intentSelect || !b.isCustom;
          bundleFlags.customType = bundleFlags.customType || b.isCustom;
        } else {
          seenIntentKeys.add(key);
        }
      }

      // Mappings validation
      const seenApps = new Set();
      (b.mappings || []).forEach((m) => {
        const mFlags = {
          purpose: !isNonEmpty(m.purpose),
          appId: !isNonEmpty(m.appId),
          duplicateApp: false
        };
        if (isNonEmpty(m.appId)) {
          if (seenApps.has(m.appId)) {
            mFlags.duplicateApp = true;
          } else {
            seenApps.add(m.appId);
          }
        }
        bundleFlags.mappings.push(mFlags);
      });

      flags.bundles.push(bundleFlags);
    });

    return flags;
  }, [row]);

  const intentsValid = useMemo(() => {
    if (!row.appBundles.length) return false;

    const usedKeys = new Set();
    for (const b of row.appBundles) {
      const key = b.isCustom ? (b.customType || '') : extractIntentKey(b.intent);

      if (b.isCustom) {
        if (!isNonEmpty(b.customType) || !isNonEmpty(b.customText)) return false;
      } else {
        if (!isNonEmpty(b.intent)) return false;
      }

      if (!isNonEmpty(key) || usedKeys.has(key)) return false;
      usedKeys.add(key);

      if (!Array.isArray(b.mappings) || !b.mappings.length) return false;
      const seenApps = new Set();
      for (const m of b.mappings) {
        if (!isNonEmpty(m.purpose) || !isNonEmpty(m.appId)) return false;
        if (seenApps.has(m.appId)) return false; // unique per intent
        seenApps.add(m.appId);
      }
    }
    return true;
  }, [row.appBundles]);

  const allValid = baseSelectorsComplete && industrySelected && intentsValid;

  // --- Payload (MERGE with savedPodio so other industries persist) ---
  const buildPayload = () => {
    const currentSelection = {
      workspace: String(row.spaceId),
      accountId: String(row.accountId),
      organization: String(row.orgId),
      domain: String(row.domain),
      app: row.appBundles.map(b => {
        const intentString = b.isCustom
          ? formatIntentLabel(b.customType, b.customText)
          : String(b.intent);
        return {
          intent: intentString,
          mappings: b.mappings.map(m => ({
            purpose: String(m.purpose),
            appId: String(m.appId),
          })),
        };
      })
    };

    // keep other saved selections for different domains (or different account/org/space)
    const others = (savedPodio || []).filter(p =>
      !( String(p.accountId)   === currentSelection.accountId &&
         String(p.organization)=== currentSelection.organization &&
         String(p.workspace)   === currentSelection.workspace &&
         String(p.domain)      === currentSelection.domain )
    );

    return { podio: [...others, currentSelection] };
  };

  // --- Save/Reset ---
  const onSave = async () => {
    if (!baseSelectorsComplete) {
      toast.error("Complete Account, Organization, and Workspace.");
      return;
    }
    if (!industrySelected) {
      toast.error("Select an Industry.");
      return;
    }
    if (!intentsValid) {
      toast.error("Fix intent/mapping validation errors (highlighted in red).");
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      if (configId) {
        const updated = await updateChatbotConfig(payload);
        if (updated?._id) {
          toast.success('Chatbot configuration updated!');
          setSavedPodio(updated.podio || []); // sync local cache after save
        } else {
          toast.success('Configuration saved.');
        }
      } else {
        const created = await createChatbotConfig(payload);
        if (created?._id) {
          setConfigId(created._id);
          setSavedPodio(created.podio || []);
          toast.success('Chatbot configuration created!');
        } else toast.success('Configuration saved.');
      }
    } catch (e) {
      toast.error('Failed to save chatbot configuration.');
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    try {
      setResetting(true);
      const res = await deleteChatbotConfig();
      if (res?.success) {
        setConfigId(null);
        setRow({ ...EMPTY_ROW });
        setSavedPodio([]); // clear cache
        toast.success('Chatbot configuration reset.');
      } else {
        toast.error('Failed to reset chatbot configuration.');
      }
    } finally {
      setResetting(false);
    }
  };



  // Decorate bundles so custom intents render their text inputs immediately
const decorateBundlesWithMode = (bundles, domain, intentsByDomain) => {
  const items = intentsByDomain?.[domain]?.items || [];
  const knownKeys = new Set(items.map(i => i.key));
  const descByKey = new Map(items.map(i => [i.key, i.description || '']));

  return (bundles || []).map((b) => {
    if (!b?.intent) return b;

    const hasColon = b.intent.includes(':');
    const key = extractIntentKey(b.intent);

    // legacy known key without desc
    if (!hasColon && knownKeys.has(key)) {
      return {
        ...b,
        isCustom: false,
        customType: '',
        customText: '',
        intent: formatIntentLabel(key, descByKey.get(key)),
      };
    }

    // colon present but key not known => custom
    if (hasColon && !knownKeys.has(key)) {
      const text = (b.intent.split(':').slice(1).join(':') || '').trim();
      return { ...b, isCustom: true, customType: key, customText: text };
    }

    // no colon and unknown => custom (user saved plain custom key earlier)
    if (!hasColon && !knownKeys.has(key)) {
      return { ...b, isCustom: true, customType: key, customText: '' };
    }

    // known dropdown intent already with desc
    return { ...b, isCustom: false, customType: '', customText: '' };
  });
};



  return (
    <>
      <Row>
        <Col xs="12">
          <WidgetsSectionTitle
            className="my-2"
            title="Attach Podio apps to Chatbot (by Intent)"
            subtitle="Select Account → Organization → Workspace → Industry → map Intents to Apps with a Purpose."
            icon="file"
          />
        </Col>

        <Col xs="12">
          <Card className="mb-3">
            <CardBody>
              {/* Account */}
              <FormGroup>
                <Label>Account</Label>
                <Input
                  type="select"
                  value={row.accountId}
                  onChange={(e) => onAccountChange(e.target.value)}
                  invalid={!isNonEmpty(row.accountId)}
                >
                  <option value="">Select an account</option>
                  {accountOptions.map((acc) => (
                    <option key={acc._id} value={acc._id}>
                      {acc.email} {acc.status ? `(${acc.status})` : ''}
                    </option>
                  ))}
                </Input>
                {!isNonEmpty(row.accountId) && <FormFeedback>Please select an account.</FormFeedback>}
              </FormGroup>

              {/* Organization */}
              <FormGroup>
                <Label>Organization</Label>
                <Input
                  type="select"
                  value={row.orgId}
                  onChange={(e) => onOrgChange(row.accountId, e.target.value)}
                  disabled={!row.accountId}
                  invalid={!isNonEmpty(row.orgId)}
                >
                  <option value="">Select an organization</option>
                  {(row.accountId ? getOrgs(row.accountId) : []).map((org) => (
                    <option key={org.org_id} value={org.org_id}>
                      {org.name}
                    </option>
                  ))}
                </Input>
                {!isNonEmpty(row.orgId) && <FormFeedback>Please select an organization.</FormFeedback>}
              </FormGroup>

              {/* Workspace */}
              <FormGroup>
                <Label>Workspace</Label>
                <Input
                  type="select"
                  value={row.spaceId}
                  onChange={(e) => onSpaceChange(row.accountId, e.target.value)}
                  disabled={!row.orgId}
                  invalid={!isNonEmpty(row.spaceId)}
                >
                  <option value="">Select a workspace</option>
                  {(row.orgId ? getSpaces(row.orgId) : []).map((space) => (
                    <option key={space.space_id} value={space.space_id}>
                      {space.name}
                    </option>
                  ))}
                </Input>
                {!isNonEmpty(row.spaceId) && <FormFeedback>Please select a workspace.</FormFeedback>}
              </FormGroup>

              {/* Industry / Domain */}
              <FormGroup>
                <Label>Industry</Label>
                <Input
                  type="select"
                  value={row.domain}
                  onChange={(e) => onIndustryChange(e.target.value)}
                  disabled={!row.spaceId}
                  invalid={!isNonEmpty(row.domain)}
                >
                  <option value="">Select an industry</option>
                  {INDUSTRY_OPTIONS.map((opt) => (
                    <option key={opt.domain} value={opt.domain}>{opt.label}</option>
                  ))}
                </Input>
                {!isNonEmpty(row.domain) && <FormFeedback>Please select an industry.</FormFeedback>}
                {row.domain && (intentsLoading ? (
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>Loading intents…</div>
                ) : availableIntents.length === 0 ? (
                  <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                    No intents found for {row.domain}. You can add custom intents below.
                  </div>
                ) : null)}
              </FormGroup>

              {/* Intent Blocks */}
              {!!row.domain && row.appBundles.map((bundle, bIdx) => {
                // NOTE: toggle buttons can be re-enabled if you want to switch modes on the fly
                const allowKey = bundle.isCustom ? (bundle.customType || '') : extractIntentKey(bundle.intent);
                return (
                  <Card key={`intent-${bIdx}`} className="mb-3 border">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold mb-0">Intent #{bIdx + 1}</h5>
                        <div className="d-flex gap-2">
                          {/* Uncomment to allow switching:
                          {bundle.isCustom ? (
                            <Button color="link" onClick={() => toggleIntentMode(bIdx, false)}>
                              Use library intent
                            </Button>
                          ) : (
                            <Button color="link" onClick={() => toggleIntentMode(bIdx, true)}>
                              Use custom intent
                            </Button>
                          )} */}
                          <Button color="danger" style={{fontSize:'12px'}} onClick={() => removeIntent(bIdx)}>Remove Intent</Button>
                        </div>
                      </div>

                      {!bundle.isCustom ? (
                        <FormGroup>
                          <Label>Intent</Label>
                          <Input
                            type="select"
                            value={extractIntentKey(bundle.intent)}
                            onChange={(e) => {
                              const key = e.target.value;
                              const found = availableIntents.find(it => it.key === key);
                              setIntentKey(bIdx, key, found?.description || '');
                            }}
                            invalid={!isNonEmpty(bundle.intent)}
                          >
                            <option value="">Select an intent</option>
                            {availableIntents.map((it) => {
                              const disabled = intentAlreadySelected(it.key, allowKey);
                              const caption = `${it.key.replace(/_/g,' ') }: ${it.description}`;
                              return (
                                <option key={it.key} value={it.key} disabled={disabled}>
                                  {caption}
                                </option>
                              );
                            })}
                          </Input>
                          {!isNonEmpty(bundle.intent) && <FormFeedback>Please select an intent.</FormFeedback>}
                        </FormGroup>
                      ) : (
                        <>
                          <FormGroup>
                            <Label>Intent Type</Label>
                            <Input
                              type="text"
                              value={bundle.customType}
                              onChange={(e) => setCustomField(bIdx, 'customType', e.target.value)}
                              placeholder="e.g., appointment_setting"
                              invalid={!isNonEmpty(bundle.customType)}
                            />
                            {!isNonEmpty(bundle.customType) && <FormFeedback>Enter intent type.</FormFeedback>}
                          </FormGroup>
                          <FormGroup>
                            <Label>Intent Text</Label>
                            <Input
                              type="textarea"
                              value={bundle.customText}
                              onChange={(e) => setCustomField(bIdx, 'customText', e.target.value)}
                              placeholder="Describe what this intent means to your chatbot."
                              rows={3}
                              invalid={!isNonEmpty(bundle.customText)}
                            />
                            {!isNonEmpty(bundle.customText) && <FormFeedback>Enter intent text/description.</FormFeedback>}
                          </FormGroup>
                        </>
                      )}

                      {(bundle.mappings || []).map((m, mIdx) => {
                        const duplicateInThisIntent = bundle.mappings.filter(x => x.appId === m.appId).length > 1;
                        return (
                          <Row key={`map-${bIdx}-${mIdx}`} className="align-items-end">
                            <Col md="7">
                              <FormGroup>
                                <Label>Purpose</Label>
                                <Input
                                  type="textarea"
                                  value={m.purpose}
                                  onChange={(e) => setMappingField(bIdx, mIdx, 'purpose', e.target.value)}
                                  placeholder="Describe why this app is used for this intent"
                                  rows={2}
                                  invalid={!isNonEmpty(m.purpose)}
                                />
                                {!isNonEmpty(m.purpose) && <FormFeedback>Purpose is required.</FormFeedback>}
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>App</Label>
                                <Input
                                  type="select"
                                  value={m.appId}
                                  onChange={(e) => setMappingField(bIdx, mIdx, 'appId', e.target.value)}
                                  disabled={!row.spaceId}
                                  invalid={!isNonEmpty(m.appId) || duplicateInThisIntent}
                                >
                                  <option value="">Select an app</option>
                                  {appOptions.map((app) => {
                                    const id = String(app.app_id);
                                    const disableSame = appDisabledInIntent(bIdx, id) && id !== m.appId;
                                    const label = app.config?.name || app.config?.item_name || app.url_label || app.app_id;
                                    return (
                                      <option key={id} value={id} disabled={disableSame}>
                                        {label}
                                      </option>
                                    );
                                  })}
                                </Input>
                                {(!isNonEmpty(m.appId) || duplicateInThisIntent) && (
                                  <FormFeedback>
                                    {!isNonEmpty(m.appId) ? 'Select an app.' : 'This app is already mapped in this intent.'}
                                  </FormFeedback>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md="1" className="mb-3">
                              <Button color="danger" style={{fontSize:'12px'}} onClick={() => removeMapping(bIdx, mIdx)}>Remove</Button>
                            </Col>
                          </Row>
                        );
                      })}

                      <Button
                        color="success"
                        style={{fontSize:'12px'}}
                        onClick={() => addMapping(bIdx)}
                        disabled={(!bundle.isCustom && !isNonEmpty(bundle.intent)) ||
                                  (bundle.isCustom && (!isNonEmpty(bundle.customType) || !isNonEmpty(bundle.customText))) ||
                                  appOptions.length === 0}
                      >
                        + Add App
                      </Button>
                    </CardBody>
                  </Card>
                );
              })}

              {/* Add intent buttons */}
              {!!row.domain && (
                <div className="d-flex gap-2">
                  <Button
                    color="success"
                    style={{ fontSize: '12px', marginRight: 8 }}
                    onClick={addIntent}
                    disabled={availableIntents.length === 0 /* may still add custom */}
                  >
                    + Add Intent
                  </Button>
                  {"  "}
                  <Button  style={{ fontSize: '12px', backgroundColor: '#FFD400', borderColor: '#FFD400', color: '#000' }} onClick={addCustomIntent}>
                    + Add Custom Intent
                  </Button>
                </div>
              )}

              {!allValid && (
                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  Complete all fields (red) and ensure each intent has unique apps with a purpose.
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Actions */}
        <Col xs="12" className="mb-3 d-flex gap-2">
          <Button
            style={{ fontSize: '12px', backgroundColor: '#16A34A', borderColor: '#16A34A', marginRight: 8 }}

            
            onClick={onSave}
            disabled={!allValid || saving || podioLoading}
          >
            {configId ? 'Save Configuration' : 'Create Configuration'}
          </Button>
 {"  "}
          <Button color="danger" style={{ fontSize: '12px' }} onClick={onReset} disabled={!configId || resetting}>
            Reset Configurations
          </Button>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (state) => ({
  // podio lookup
  podioLoading: state.podioLookup?.loading,
  accounts: state.podioLookup?.accounts || [],
  orgsByAccount: state.podioLookup?.orgsByAccount || {},
  spacesByOrg: state.podioLookup?.spacesByOrg || {},
  appsBySpace: state.podioLookup?.appsBySpace || {},

  // intents
  intentsLoading: state.intents?.loading,
  intentsByDomain: state.intents?.byDomain || {},
});

export default connect(mapStateToProps, {
  fetchPodioAccounts,
  fetchPodioOrgs,
  fetchPodioSpaces,
  fetchPodioApps,
  fetchIntentsByDomain,
  fetchChatbotConfig,
  createChatbotConfig,
  updateChatbotConfig,
  deleteChatbotConfig,
})(ChatbotConfig);



// // New with multiple apps + intents + custom intents
// import React, { useEffect, useMemo, useState } from 'react';
// import { Row, Col, Card, CardBody, Button, Input, Label, FormGroup, FormFeedback } from 'reactstrap';
// import { connect } from 'react-redux';
// import WidgetsSectionTitle from '../../../WidgetsSectionTitle/WidgetSectionTitle';
// import { toast } from 'react-toastify';

// // Chatbot config CRUD
// import {
//   fetchChatbotConfig,
//   createChatbotConfig,
//   updateChatbotConfig,
//   deleteChatbotConfig,
// } from '../../../Redux/Actions/AI/aichatbot';

// // Podio lookups
// import {
//   fetchPodioAccounts,
//   fetchPodioOrgs,
//   fetchPodioSpaces,
//   fetchPodioApps,
// } from '../../../Redux/Actions/AI/podiolookup';

// // Intents by domain
// import { fetchIntentsByDomain } from '../../../Redux/Actions/AI/Intents';

// const EMPTY_ROW = {
//   accountId: '',
//   orgId: '',
//   spaceId: '',
//   domain: '',
//   // [{ intent: (full label) '', isCustom?:bool, customType?:'', customText?:'',
//   //    mappings:[{ purpose:'', appId:'' }] }]
//   appBundles: []
// };

// // UI industry→domain mapping
// const INDUSTRY_OPTIONS = [
//   { label: 'Real Estate',   domain: 'REAL_ESTATE' },
//   { label: 'Health Care',   domain: 'HEALTH_CARE' },
//   { label: 'Product',       domain: 'PRODUCT' },
//   { label: 'E-commerce',    domain: 'E_COMMERCE' },
//   { label: 'Construction',  domain: 'CONSTRUCTION' },
//   { label: 'Marine',        domain: 'MARINE' },
// ];

// // Helpers to format/parse intent labels
// const formatIntentLabel = (key, description) =>
//   `${String(key || '').trim()}: ${String(description || '').trim()}`.trim();

// const extractIntentKey = (storedIntent) =>
//   (storedIntent || '').split(':')[0].trim();

// const isNonEmpty = (v) => typeof v === 'string' && v.trim().length > 0;

// const ChatbotConfig = ({
//   // podio lookup
//   podioLoading,
//   accounts,
//   orgsByAccount,
//   spacesByOrg,
//   appsBySpace,

//   // intents
//   intentsLoading,
//   intentsByDomain,

//   // actions
//   fetchPodioAccounts,
//   fetchPodioOrgs,
//   fetchPodioSpaces,
//   fetchPodioApps,
//   fetchIntentsByDomain,

//   // chatbot config
//   fetchChatbotConfig,
//   createChatbotConfig,
//   updateChatbotConfig,
//   deleteChatbotConfig,
// }) => {
//   const [row, setRow] = useState({ ...EMPTY_ROW });
//   const [configId, setConfigId] = useState(null);
//   const [saving, setSaving] = useState(false);
//   const [resetting, setResetting] = useState(false);

//   const getOrgs   = (accountId) => orgsByAccount?.[accountId] || [];
//   const getSpaces = (orgId)     => spacesByOrg?.[orgId] || [];
//   const getApps   = (spaceId)   => appsBySpace?.[spaceId] || [];

//   // --- Init: accounts + existing config ---
//   useEffect(() => {
//     fetchPodioAccounts();
//     (async () => {
//       const res = await fetchChatbotConfig();
//       if (res?._id && Array.isArray(res.podio) && res.podio.length > 0) {
//         const first = res.podio[0];
//         setConfigId(res._id);

//         // Hydrate base selectors
//         const initialRow = {
//           accountId: first.accountId || '',
//           orgId: first.organization || '',
//           spaceId: first.workspace || '',
//           domain: first.domain || '',
//           appBundles: Array.isArray(first.app)
//             ? first.app.map((b) => ({
//                 intent: b.intent || '',
//                 isCustom: false,        // may switch to true after we check against domain intents
//                 customType: '',
//                 customText: '',
//                 mappings: Array.isArray(b.mappings)
//                   ? b.mappings.map(m => ({ purpose: m.purpose || '', appId: m.appId || '' }))
//                   : [{ purpose: '', appId: '' }]
//               }))
//             : []
//         };
//         setRow(initialRow);

//         // Preload cascades
//         if (first.accountId) await fetchPodioOrgs(first.accountId);
//         if (first.accountId && first.organization) await fetchPodioSpaces(first.accountId, first.organization);
//         if (first.accountId && first.workspace) await fetchPodioApps(first.accountId, first.workspace);

//         // Preload intents for domain if present
//         if (first.domain) await fetchIntentsByDomain(first.domain);
//       }
//     })();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // After intents load, 1) upgrade legacy (key-only) intents to "key: desc",
//   // 2) determine which bundles are "custom" vs "dropdown" by checking the key in the loaded set.
//   useEffect(() => {
//     if (!row.domain) return;
//     const domainData = intentsByDomain?.[row.domain];
//     const items = domainData?.items || [];
//     if (!items.length) return;

//     const descByKey = new Map(items.map(it => [it.key, it.description || '']));
//     const knownKeys = new Set(items.map(it => it.key));

//     let changed = false;
//     const upgraded = row.appBundles.map(b => {
//       if (!b?.intent) return b;

//       const hasColon = b.intent.includes(':');
//       let key = extractIntentKey(b.intent);

//       // If legacy (no colon) and known key → expand with description
//       if (!hasColon && knownKeys.has(key)) {
//         const full = formatIntentLabel(key, descByKey.get(key));
//         changed = true;
//         return { ...b, intent: full, isCustom: false, customType: '', customText: '' };
//       }

//       // If colon exists but key not in known set → treat as custom
//       if (hasColon && !knownKeys.has(key)) {
//         const text = (b.intent.split(':').slice(1).join(':') || '').trim();
//         if (b.isCustom && b.customType === key && b.customText === text) return b;
//         changed = true;
//         return { ...b, isCustom: true, customType: key, customText: text };
//       }

//       // If no colon and unknown key → custom (user saved earlier with a plain custom key)
//       if (!hasColon && !knownKeys.has(key)) {
//         changed = true;
//         return { ...b, isCustom: true, customType: key, customText: '' };
//       }

//       // Known dropdown intent
//       return { ...b, isCustom: false, customType: '', customText: '' };
//     });

//     if (changed) setRow(r => ({ ...r, appBundles: upgraded }));
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [row.domain, intentsByDomain]);

//   // --- Select lists ---
//   const accountOptions = accounts;
//   const orgOptions   = row.accountId ? getOrgs(row.accountId) : [];
//   const spaceOptions = row.orgId ? getSpaces(row.orgId) : [];
//   const appOptions   = row.spaceId ? getApps(row.spaceId) : [];

//   const availableIntents = useMemo(() => {
//     const domainData = intentsByDomain?.[row.domain];
//     const items = domainData?.items || [];
//     return items; // [{key,description}]
//   }, [intentsByDomain, row.domain]);

//   // --- Cascading handlers ---
//   const onAccountChange = async (newAccountId) => {
//     setRow({ ...EMPTY_ROW, accountId: newAccountId });
//     if (newAccountId) await fetchPodioOrgs(newAccountId);
//   };

//   const onOrgChange = async (accountId, newOrgId) => {
//     setRow((r) => ({ ...r, orgId: newOrgId, spaceId: '', domain: '', appBundles: [] }));
//     if (accountId && newOrgId) await fetchPodioSpaces(accountId, newOrgId);
//   };

//   const onSpaceChange = async (accountId, newSpaceId) => {
//     setRow((r) => ({ ...r, spaceId: newSpaceId, domain: '', appBundles: [] }));
//     if (accountId && newSpaceId) await fetchPodioApps(accountId, newSpaceId);
//   };

//   const onIndustryChange = async (domain) => {
//     setRow((r) => ({ ...r, domain, appBundles: [] }));
//     if (domain) await fetchIntentsByDomain(domain);
//   };

//   // --- Intent helpers ---
//   const currentBundleKey = (bundle) => {
//     return bundle.isCustom ? (bundle.customType || '') : extractIntentKey(bundle.intent);
//   };

//   const selectedIntentKeys = new Set(
//     row.appBundles.map(b => currentBundleKey(b)).filter(Boolean)
//   );

//   const intentAlreadySelected = (key, allowKey) => {
//     if (allowKey && key === allowKey) return false;
//     return selectedIntentKeys.has(key);
//   };

//   const addIntent = () => {
//     if (!row.domain) {
//       toast.error("Select an industry first.");
//       return;
//     }
//     setRow((r) => ({
//       ...r,
//       appBundles: [
//         ...r.appBundles,
//         { intent: '', isCustom: false, customType: '', customText: '', mappings: [{ purpose: '', appId: '' }] }
//       ],
//     }));
//   };

//   const addCustomIntent = () => {
//     if (!row.domain) {
//       toast.error("Select an industry first.");
//       return;
//     }
//     setRow((r) => ({
//       ...r,
//       appBundles: [
//         ...r.appBundles,
//         { intent: '', isCustom: true, customType: '', customText: '', mappings: [{ purpose: '', appId: '' }] }
//       ],
//     }));
//   };

//   const removeIntent = (idx) => {
//     setRow((r) => {
//       const copy = [...r.appBundles];
//       copy.splice(idx, 1);
//       return { ...r, appBundles: copy };
//     });
//   };

//   const toggleIntentMode = (idx, toCustom) => {
//     setRow((r) => {
//       const copy = [...r.appBundles];
//       const bundle = { ...copy[idx] };
//       if (toCustom) {
//         // switch to custom, keep mappings
//         bundle.isCustom = true;
//         // if it had a dropdown intent, prefill type/text from it
//         const k = extractIntentKey(bundle.intent);
//         const t = (bundle.intent.split(':').slice(1).join(':') || '').trim();
//         bundle.customType = k || '';
//         bundle.customText = t || '';
//         bundle.intent = ''; // clear dropdown label
//       } else {
//         // switch to dropdown, clear custom fields
//         bundle.isCustom = false;
//         bundle.customType = '';
//         bundle.customText = '';
//         // keep bundle.intent as is (will be set via dropdown)
//       }
//       copy[idx] = bundle;
//       return { ...r, appBundles: copy };
//     });
//   };

//   const setIntentKey = (idx, intentKey, description) => {
//     const fullLabel = formatIntentLabel(intentKey, description);
//     setRow((r) => {
//       const copy = [...r.appBundles];
//       copy[idx] = { ...copy[idx], intent: fullLabel };
//       return { ...r, appBundles: copy };
//     });
//   };

//   // Mapping rows per intent
//   const addMapping = (intentIdx) => {
//     setRow((r) => {
//       const bundles = [...r.appBundles];
//       const item = bundles[intentIdx];
//       item.mappings = [...item.mappings, { purpose: '', appId: '' }];
//       return { ...r, appBundles: bundles };
//     });
//   };

//   const removeMapping = (intentIdx, mapIdx) => {
//     setRow((r) => {
//       const bundles = [...r.appBundles];
//       const item = bundles[intentIdx];
//       const maps = [...item.mappings];
//       maps.splice(mapIdx, 1);
//       item.mappings = maps.length ? maps : [{ purpose: '', appId: '' }]; // keep at least one row
//       return { ...r, appBundles: bundles };
//     });
//   };

//   const setMappingField = (intentIdx, mapIdx, field, value) => {
//     setRow((r) => {
//       const bundles = [...r.appBundles];
//       const item = bundles[intentIdx];
//       const maps = [...item.mappings];
//       maps[mapIdx] = { ...maps[mapIdx], [field]: value };
//       item.mappings = maps;
//       return { ...r, appBundles: bundles };
//     });
//   };

//   const setCustomField = (idx, field, value) => {
//     setRow((r) => {
//       const copy = [...r.appBundles];
//       copy[idx] = { ...copy[idx], [field]: value };
//       return { ...r, appBundles: copy };
//     });
//   };

//   // Disable app used within the same intent
//   const appDisabledInIntent = (intentIdx, appId) => {
//     const bundle = row.appBundles[intentIdx];
//     return bundle.mappings.some((m) => m.appId === appId);
//   };

//   // --- Validation ---
//   const baseSelectorsComplete = row.accountId && row.orgId && row.spaceId;
//   const industrySelected = !!row.domain;

//   // Build per-field invalid flags for red borders
//   const invalidFlags = useMemo(() => {
//     const flags = {
//       accountId: !isNonEmpty(row.accountId),
//       orgId: !isNonEmpty(row.orgId),
//       spaceId: !isNonEmpty(row.spaceId),
//       domain: !isNonEmpty(row.domain),
//       bundles: []
//     };

//     // Intent key uniqueness check
//     const seenIntentKeys = new Set();

//     row.appBundles.forEach((b, bIdx) => {
//       const key = currentBundleKey(b);
//       const bundleFlags = {
//         isCustom: !!b.isCustom,
//         intentSelect: false,
//         customType: false,
//         customText: false,
//         mappings: []
//       };

//       if (b.isCustom) {
//         if (!isNonEmpty(b.customType)) bundleFlags.customType = true;
//         if (!isNonEmpty(b.customText)) bundleFlags.customText = true;
//       } else {
//         if (!isNonEmpty(b.intent)) bundleFlags.intentSelect = true;
//       }

//       // Duplicate intent key?
//       if (isNonEmpty(key)) {
//         if (seenIntentKeys.has(key)) {
//           // mark as invalid (we’ll prevent save via overall validation)
//           bundleFlags.intentSelect = bundleFlags.intentSelect || !b.isCustom;
//           bundleFlags.customType = bundleFlags.customType || b.isCustom;
//         } else {
//           seenIntentKeys.add(key);
//         }
//       } else {
//         // no key means either dropdown not chosen or customType missing; already marked above
//       }

//       // Mappings validation
//       const seenApps = new Set();
//       (b.mappings || []).forEach((m) => {
//         const mFlags = {
//           purpose: !isNonEmpty(m.purpose),
//           appId: !isNonEmpty(m.appId),
//           duplicateApp: false
//         };
//         if (isNonEmpty(m.appId)) {
//           if (seenApps.has(m.appId)) {
//             mFlags.duplicateApp = true;
//           } else {
//             seenApps.add(m.appId);
//           }
//         }
//         bundleFlags.mappings.push(mFlags);
//       });

//       flags.bundles.push(bundleFlags);
//     });

//     return flags;
//   }, [row]);

//   const intentsValid = useMemo(() => {
//     if (!row.appBundles.length) return false;

//     const usedKeys = new Set();
//     for (const b of row.appBundles) {
//       const key = currentBundleKey(b);

//       if (b.isCustom) {
//         if (!isNonEmpty(b.customType) || !isNonEmpty(b.customText)) return false;
//       } else {
//         if (!isNonEmpty(b.intent)) return false;
//       }

//       if (!isNonEmpty(key) || usedKeys.has(key)) return false;
//       usedKeys.add(key);

//       if (!Array.isArray(b.mappings) || !b.mappings.length) return false;
//       const seenApps = new Set();
//       for (const m of b.mappings) {
//         if (!isNonEmpty(m.purpose) || !isNonEmpty(m.appId)) return false;
//         if (seenApps.has(m.appId)) return false; // unique per intent
//         seenApps.add(m.appId);
//       }
//     }
//     return true;
//   }, [row.appBundles]);

//   const allValid = baseSelectorsComplete && industrySelected && intentsValid;

//   // --- Payload ---
//   const buildPayload = () => ({
//     podio: [{
//       workspace: String(row.spaceId),
//       accountId: String(row.accountId),
//       organization: String(row.orgId),
//       domain: String(row.domain),
//       app: row.appBundles.map(b => {
//         const intentString = b.isCustom
//           ? formatIntentLabel(b.customType, b.customText)
//           : String(b.intent);
//         return {
//           intent: intentString,
//           mappings: b.mappings.map(m => ({
//             purpose: String(m.purpose),
//             appId: String(m.appId),
//           })),
//         };
//       })
//     }]
//   });

//   // --- Save/Reset ---
//   const onSave = async () => {
//     if (!baseSelectorsComplete) {
//       toast.error("Complete Account, Organization, and Workspace.");
//       return;
//     }
//     if (!industrySelected) {
//       toast.error("Select an Industry.");
//       return;
//     }
//     if (!intentsValid) {
//       toast.error("Fix intent/mapping validation errors (highlighted in red).");
//       return;
//     }

//     try {
//       setSaving(true);
//       const payload = buildPayload();
//       if (configId) {
//         const updated = await updateChatbotConfig(payload);
//         if (updated?._id) toast.success('Chatbot configuration updated!');
//         else toast.success('Configuration saved.');
//       } else {
//         const created = await createChatbotConfig(payload);
//         if (created?._id) {
//           setConfigId(created._id);
//           toast.success('Chatbot configuration created!');
//         } else toast.success('Configuration saved.');
//       }
//     } catch (e) {
//       toast.error('Failed to save chatbot configuration.');
//     } finally {
//       setSaving(false);
//     }
//   };

//   const onReset = async () => {
//     try {
//       setResetting(true);
//       const res = await deleteChatbotConfig();
//       if (res?.success) {
//         setConfigId(null);
//         setRow({ ...EMPTY_ROW });
//         toast.success('Chatbot configuration reset.');
//       } else {
//         toast.error('Failed to reset chatbot configuration.');
//       }
//     } finally {
//       setResetting(false);
//     }
//   };

//   return (
//     <>
//       <Row>
//         <Col xs="12">
//           <WidgetsSectionTitle
//             className="my-2"
//             title="Attach Podio apps to Chatbot (by Intent)"
//             subtitle="Select Account → Organization → Workspace → Industry → map Intents to Apps with a Purpose."
//             icon="settings"
//           />
//         </Col>

//         <Col xs="12">
//           <Card className="mb-3">
//             <CardBody>
//               {/* Account */}
//               <FormGroup>
//                 <Label>Account</Label>
//                 <Input
//                   type="select"
//                   value={row.accountId}
//                   onChange={(e) => onAccountChange(e.target.value)}
//                   invalid={invalidFlags.accountId}
//                 >
//                   <option value="">Select an account</option>
//                   {accountOptions.map((acc) => (
//                     <option key={acc._id} value={acc._id}>
//                       {acc.email} {acc.status ? `(${acc.status})` : ''}
//                     </option>
//                   ))}
//                 </Input>
//                 {invalidFlags.accountId && <FormFeedback>Please select an account.</FormFeedback>}
//               </FormGroup>

//               {/* Organization */}
//               <FormGroup>
//                 <Label>Organization</Label>
//                 <Input
//                   type="select"
//                   value={row.orgId}
//                   onChange={(e) => onOrgChange(row.accountId, e.target.value)}
//                   disabled={!row.accountId}
//                   invalid={invalidFlags.orgId}
//                 >
//                   <option value="">Select an organization</option>
//                   {(row.accountId ? getOrgs(row.accountId) : []).map((org) => (
//                     <option key={org.org_id} value={org.org_id}>
//                       {org.name}
//                     </option>
//                   ))}
//                 </Input>
//                 {invalidFlags.orgId && <FormFeedback>Please select an organization.</FormFeedback>}
//               </FormGroup>

//               {/* Workspace */}
//               <FormGroup>
//                 <Label>Workspace</Label>
//                 <Input
//                   type="select"
//                   value={row.spaceId}
//                   onChange={(e) => onSpaceChange(row.accountId, e.target.value)}
//                   disabled={!row.orgId}
//                   invalid={invalidFlags.spaceId}
//                 >
//                   <option value="">Select a workspace</option>
//                   {(row.orgId ? getSpaces(row.orgId) : []).map((space) => (
//                     <option key={space.space_id} value={space.space_id}>
//                       {space.name}
//                     </option>
//                   ))}
//                 </Input>
//                 {invalidFlags.spaceId && <FormFeedback>Please select a workspace.</FormFeedback>}
//               </FormGroup>

//               {/* Industry / Domain */}
//               <FormGroup>
//                 <Label>Industry</Label>
//                 <Input
//                   type="select"
//                   value={row.domain}
//                   onChange={(e) => onIndustryChange(e.target.value)}
//                   disabled={!row.spaceId}
//                   invalid={invalidFlags.domain}
//                 >
//                   <option value="">Select an industry</option>
//                   {INDUSTRY_OPTIONS.map((opt) => (
//                     <option key={opt.domain} value={opt.domain}>{opt.label}</option>
//                   ))}
//                 </Input>
//                 {invalidFlags.domain && <FormFeedback>Please select an industry.</FormFeedback>}
//                 {row.domain && (intentsLoading ? (
//                   <div className="text-muted mt-1" style={{ fontSize: 12 }}>Loading intents…</div>
//                 ) : availableIntents.length === 0 ? (
//                   <div className="text-danger mt-1" style={{ fontSize: 12 }}>
//                     No intents found for {row.domain}. You can add custom intents below.
//                   </div>
//                 ) : null)}
//               </FormGroup>

//               {/* Intent Blocks */}
//               {!!row.domain && row.appBundles.map((bundle, bIdx) => {
//                 const flags = invalidFlags.bundles[bIdx] || { mappings: [] };
//                 const allowKey = currentBundleKey(bundle);

//                 return (
//                   <Card key={`intent-${bIdx}`} className="mb-3 border">
//                     <CardBody>
//                       <div className="d-flex justify-content-between align-items-center mb-2">
//                         <h6 className="mb-0">Intent #{bIdx + 1}</h6>
//                         <div className="d-flex gap-2">
//                           {/* {bundle.isCustom ? (
//                             <Button color="link" onClick={() => toggleIntentMode(bIdx, false)}>
//                               Use library intent
//                             </Button>
//                           ) : (
//                             <Button color="link" onClick={() => toggleIntentMode(bIdx, true)}>
//                               Use custom intent
//                             </Button>
//                           )} */}
//                           <Button color="link" onClick={() => removeIntent(bIdx)}>
//                             Remove
//                           </Button>
//                         </div>
//                       </div>

//                       {/* Intent selection OR custom fields */}
//                       {!bundle.isCustom ? (
//                         <FormGroup>
//                           <Label>Intent</Label>
//                           <Input
//                             type="select"
//                             value={extractIntentKey(bundle.intent)}
//                             onChange={(e) => {
//                               const key = e.target.value;
//                               const found = availableIntents.find(it => it.key === key);
//                               setIntentKey(bIdx, key, found?.description || '');
//                             }}
//                             invalid={flags.intentSelect}
//                           >
//                             <option value="">Select an intent</option>
//                             {availableIntents.map((it) => {
//                               const disabled = intentAlreadySelected(it.key, allowKey);
//                               const caption = `${it.key.replace(/_/g,' ') }: ${it.description}`;
//                               return (
//                                 <option key={it.key} value={it.key} disabled={disabled}>
//                                   {caption}
//                                 </option>
//                               );
//                             })}
//                           </Input>
//                           {flags.intentSelect && <FormFeedback>Please select an intent.</FormFeedback>}
//                         </FormGroup>
//                       ) : (
//                         <>
//                           <FormGroup>
//                             <Label>Intent Type</Label>
//                             <Input
//                               type="text"
//                               value={bundle.customType}
//                               onChange={(e) => setCustomField(bIdx, 'customType', e.target.value)}
//                               placeholder="e.g., appointment_setting"
//                               invalid={flags.customType}
//                             />
//                             {flags.customType && <FormFeedback>Enter intent type.</FormFeedback>}
//                           </FormGroup>
//                           <FormGroup>
//                             <Label>Intent Text</Label>
//                             <Input
//                               type="textarea"
//                               value={bundle.customText}
//                               onChange={(e) => setCustomField(bIdx, 'customText', e.target.value)}
//                               placeholder="Describe what this intent means to your chatbot."
//                               invalid={flags.customText}
//                               rows={3}
//                             />
//                             {flags.customText && <FormFeedback>Enter intent text/description.</FormFeedback>}
//                           </FormGroup>
//                         </>
//                       )}

//                       {/* Mappings for this intent */}
//                       {(bundle.mappings || []).map((m, mIdx) => {
//                         const mFlags = flags.mappings[mIdx] || {};
//                         return (
//                           <Row key={`map-${bIdx}-${mIdx}`} className="align-items-end">
//                             <Col md="7">
//                               <FormGroup>
//                                 <Label>Purpose</Label>
//                                 <Input
//                                   type="textarea"
//                                   value={m.purpose}
//                                   onChange={(e) => setMappingField(bIdx, mIdx, 'purpose', e.target.value)}
//                                   placeholder="Describe why this app is used for this intent"
//                                   invalid={mFlags.purpose}
//                                   rows={2}
//                                 />
//                                 {mFlags.purpose && <FormFeedback>Purpose is required.</FormFeedback>}
//                               </FormGroup>
//                             </Col>
//                             <Col md="4">
//                               <FormGroup>
//                                 <Label>App</Label>
//                                 <Input
//                                   type="select"
//                                   value={m.appId}
//                                   onChange={(e) => setMappingField(bIdx, mIdx, 'appId', e.target.value)}
//                                   disabled={!row.spaceId}
//                                   invalid={mFlags.appId || mFlags.duplicateApp}
//                                 >
//                                   <option value="">Select an app</option>
//                                   {appOptions.map((app) => {
//                                     const id = String(app.app_id);
//                                     const disableSame = appDisabledInIntent(bIdx, id) && id !== m.appId;
//                                     const label = app.config?.name || app.config?.item_name || app.url_label || app.app_id;
//                                     return (
//                                       <option key={id} value={id} disabled={disableSame}>
//                                         {label}
//                                       </option>
//                                     );
//                                   })}
//                                 </Input>
//                                 {(mFlags.appId || mFlags.duplicateApp) && (
//                                   <FormFeedback>
//                                     {mFlags.appId ? 'Select an app.' : 'This app is already mapped in this intent.'}
//                                   </FormFeedback>
//                                 )}
//                               </FormGroup>
//                             </Col>
//                             <Col md="1" className="mb-3">
//                               <Button color="link" onClick={() => removeMapping(bIdx, mIdx)}>
//                                 Remove
//                               </Button>
//                             </Col>
//                           </Row>
//                         );
//                       })}

//                       <Button
//                         color="secondary"
//                         onClick={() => addMapping(bIdx)}
//                         disabled={(!bundle.isCustom && !isNonEmpty(bundle.intent)) ||
//                                   (bundle.isCustom && (!isNonEmpty(bundle.customType) || !isNonEmpty(bundle.customText))) ||
//                                   appOptions.length === 0}
//                       >
//                         + Add App
//                       </Button>
//                     </CardBody>
//                   </Card>
//                 );
//               })}

//               {/* Add intent buttons */}
//               {!!row.domain && (
//                 <div className="d-flex gap-2">
//                   <Button
//                     color="info"
//                     onClick={addIntent}
//                     disabled={availableIntents.length === 0 /* may still add custom */}
//                   >
//                     + Add Intent
//                   </Button>
//                   <Button
//                     color="warning"
//                     onClick={addCustomIntent}
//                   >
//                     + Add Custom Intent
//                   </Button>
//                 </div>
//               )}

//               {/* Validation hint */}
//               {!allValid && (
//                 <div className="text-muted mt-2" style={{ fontSize: 12 }}>
//                   Complete all fields (red) and ensure each intent has unique apps with a purpose.
//                 </div>
//               )}
//             </CardBody>
//           </Card>
//         </Col>

//         {/* Actions */}
//         <Col xs="12" className="mb-3 d-flex gap-2">
//           <Button
//             color="success"
//             onClick={onSave}
//             disabled={!allValid || saving || podioLoading}
//           >
//             {configId ? 'Save Configuration' : 'Create Configuration'}
//           </Button>

//           <Button color="danger" onClick={onReset} disabled={!configId || resetting}>
//             Reset Configurations
//           </Button>
//         </Col>
//       </Row>
//     </>
//   );
// };

// const mapStateToProps = (state) => ({
//   // podio lookup
//   podioLoading: state.podioLookup?.loading,
//   accounts: state.podioLookup?.accounts || [],
//   orgsByAccount: state.podioLookup?.orgsByAccount || {},
//   spacesByOrg: state.podioLookup?.spacesByOrg || {},
//   appsBySpace: state.podioLookup?.appsBySpace || {},

//   // intents
//   intentsLoading: state.intents?.loading,
//   intentsByDomain: state.intents?.byDomain || {},
// });

// export default connect(mapStateToProps, {
//   fetchPodioAccounts,
//   fetchPodioOrgs,
//   fetchPodioSpaces,
//   fetchPodioApps,
//   fetchIntentsByDomain,
//   fetchChatbotConfig,
//   createChatbotConfig,
//   updateChatbotConfig,
//   deleteChatbotConfig,
// })(ChatbotConfig);






















// // New with multiple apps + intents
// import React, { useEffect, useMemo, useState } from 'react';
// import { Row, Col, Card, CardBody, Button, Input, Label, FormGroup } from 'reactstrap';
// import { connect } from 'react-redux';
// import WidgetsSectionTitle from '../../../WidgetsSectionTitle/WidgetSectionTitle';
// import { toast } from 'react-toastify';

// // Chatbot config CRUD
// import {
//   fetchChatbotConfig,
//   createChatbotConfig,
//   updateChatbotConfig,
//   deleteChatbotConfig,
// } from '../../../Redux/Actions/AI/aichatbot';

// // Podio lookups
// import {
//   fetchPodioAccounts,
//   fetchPodioOrgs,
//   fetchPodioSpaces,
//   fetchPodioApps,
// } from '../../../Redux/Actions/AI/podiolookup';

// // NEW: Intents by domain
// import { fetchIntentsByDomain } from '../../../Redux/Actions/AI/Intents';

// const EMPTY_ROW = {
//   accountId: '',
//   orgId: '',
//   spaceId: '',
//   domain: '',   // NEW
//   appBundles: [] // [{ intent: '', mappings: [{ purpose:'', appId:'' }] }]
// };

// // UI industry→domain mapping
// const INDUSTRY_OPTIONS = [
//   { label: 'Real Estate',   domain: 'REAL_ESTATE' },
//   { label: 'Health Care',   domain: 'HEALTH_CARE' },
//   { label: 'Product',       domain: 'PRODUCT' },
//   { label: 'E-commerce',    domain: 'E_COMMERCE' },
//   { label: 'Construction',  domain: 'CONSTRUCTION' },
//   { label: 'Marine',        domain: 'MARINE' },
// ];

// const ChatbotConfig = ({
//   // lookup state
//   podioLoading,
//   accounts,
//   orgsByAccount,
//   spacesByOrg,
//   appsBySpace,

//   // intents
//   intentsLoading,
//   intentsByDomain,

//   // actions
//   fetchPodioAccounts,
//   fetchPodioOrgs,
//   fetchPodioSpaces,
//   fetchPodioApps,
//   fetchIntentsByDomain,

//   // chatbot config
//   fetchChatbotConfig,
//   createChatbotConfig,
//   updateChatbotConfig,
//   deleteChatbotConfig,
// }) => {
//   const [row, setRow] = useState({ ...EMPTY_ROW });
//   const [configId, setConfigId] = useState(null);
//   const [saving, setSaving] = useState(false);
//   const [resetting, setResetting] = useState(false);

//   const getOrgs = (accountId) => orgsByAccount?.[accountId] || [];
//   const getSpaces = (orgId) => spacesByOrg?.[orgId] || [];
//   const getApps = (spaceId) => appsBySpace?.[spaceId] || [];


//   // Helpers to format/parse intent labels
// const formatIntentLabel = (key, description) =>
//   `${key}: ${description || ''}`.trim();

// const extractIntentKey = (storedIntent) =>
//   (storedIntent || '').split(':')[0].trim();

//   // --- Init: accounts + existing config ---
//   useEffect(() => {
//     fetchPodioAccounts();
//     (async () => {
//       const res = await fetchChatbotConfig();
//       if (res?._id && Array.isArray(res.podio) && res.podio.length > 0) {
//         const first = res.podio[0];
//         setConfigId(res._id);

//         // Hydrate base selectors
//         const initialRow = {
//           accountId: first.accountId || '',
//           orgId: first.organization || '',
//           spaceId: first.workspace || '',
//           domain: first.domain || '',
//           appBundles: Array.isArray(first.app)
//             ? first.app.map((b) => ({
//                 intent: b.intent || '',
//                 mappings: Array.isArray(b.mappings)
//                   ? b.mappings.map(m => ({ purpose: m.purpose || '', appId: m.appId || '' }))
//                   : []
//               }))
//             : []
//         };
//         setRow(initialRow);

//         // Preload cascades
//         if (first.accountId) await fetchPodioOrgs(first.accountId);
//         if (first.accountId && first.organization) await fetchPodioSpaces(first.accountId, first.organization);
//         if (first.accountId && first.workspace) await fetchPodioApps(first.accountId, first.workspace);

//         // Preload intents for domain if present
//         if (first.domain) await fetchIntentsByDomain(first.domain);
//       }
//     })();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);


// useEffect(() => {
//   if (!row.domain) return;
//   const domainData = intentsByDomain?.[row.domain];
//   const items = domainData?.items || [];
//   if (!items.length) return;

//   // Map for quick lookup
//   const descByKey = new Map(items.map(it => [it.key, it.description || '']));

//   let changed = false;
//   const upgraded = row.appBundles.map(b => {
//     if (!b?.intent || b.intent.includes(':')) return b; // already upgraded
//     const key = extractIntentKey(b.intent);
//     const desc = descByKey.get(key);
//     if (!desc) return b; // unknown key; leave as is
//     changed = true;
//     return { ...b, intent: formatIntentLabel(key, desc) };
//   });

//   if (changed) {
//     setRow(r => ({ ...r, appBundles: upgraded }));
//   }
// }, [row.domain, intentsByDomain, row.appBundles]); // eslint-disable-line








//   // --- Select lists ---
//   const accountOptions = accounts;
//   const orgOptions = row.accountId ? getOrgs(row.accountId) : [];
//   const spaceOptions = row.orgId ? getSpaces(row.orgId) : [];
//   const appOptions = row.spaceId ? getApps(row.spaceId) : [];

//   const availableIntents = useMemo(() => {
//     const domainData = intentsByDomain?.[row.domain];
//     const items = domainData?.items || [];
//     return items; // [{key,description}]
//   }, [intentsByDomain, row.domain]);

//   // --- Cascading handlers ---
//   const onAccountChange = async (newAccountId) => {
//     setRow({ ...EMPTY_ROW, accountId: newAccountId });
//     if (newAccountId) await fetchPodioOrgs(newAccountId);
//   };

//   const onOrgChange = async (accountId, newOrgId) => {
//     setRow((r) => ({ ...r, orgId: newOrgId, spaceId: '', domain: '', appBundles: [] }));
//     if (accountId && newOrgId) await fetchPodioSpaces(accountId, newOrgId);
//   };

//   const onSpaceChange = async (accountId, newSpaceId) => {
//     setRow((r) => ({ ...r, spaceId: newSpaceId, domain: '', appBundles: [] }));
//     if (accountId && newSpaceId) await fetchPodioApps(accountId, newSpaceId);
//   };

//   const onIndustryChange = async (domain) => {
//     setRow((r) => ({ ...r, domain, appBundles: [] }));
//     if (domain) await fetchIntentsByDomain(domain);
//   };

//   // --- Intent helpers ---
// const selectedIntentKeys = new Set(
//   row.appBundles
//     .map(b => extractIntentKey(b.intent))
//     .filter(Boolean)
// );

// const intentAlreadySelected = (key, currentBundleKey) => {
//   // allow the one currently selected in this block
//   if (currentBundleKey && key === currentBundleKey) return false;
//   return selectedIntentKeys.has(key);
// };


//   const addIntent = () => {
//     if (!row.domain) {
//       toast.error("Select an industry first.");
//       return;
//     }
//     setRow((r) => ({
//       ...r,
//       appBundles: [
//         ...r.appBundles,
//         { intent: '', mappings: [{ purpose: '', appId: '' }] }
//       ],
//     }));
//   };

//   const removeIntent = (idx) => {
//     setRow((r) => {
//       const copy = [...r.appBundles];
//       copy.splice(idx, 1);
//       return { ...r, appBundles: copy };
//     });
//   };

// const setIntentKey = (idx, intentKey) => {
//   // find description from availableIntents
//   const found = availableIntents.find(it => it.key === intentKey);
//   const fullLabel = formatIntentLabel(intentKey, found?.description || '');
//   setRow((r) => {
//     const copy = [...r.appBundles];
//     copy[idx] = { ...copy[idx], intent: fullLabel };
//     return { ...r, appBundles: copy };
//   });
// };

//   // Mapping rows per intent
//   const addMapping = (intentIdx) => {
//     setRow((r) => {
//       const bundles = [...r.appBundles];
//       const item = bundles[intentIdx];
//       item.mappings = [...item.mappings, { purpose: '', appId: '' }];
//       return { ...r, appBundles: bundles };
//     });
//   };

//   const removeMapping = (intentIdx, mapIdx) => {
//     setRow((r) => {
//       const bundles = [...r.appBundles];
//       const item = bundles[intentIdx];
//       const maps = [...item.mappings];
//       maps.splice(mapIdx, 1);
//       item.mappings = maps.length ? maps : [{ purpose: '', appId: '' }]; // keep at least one row
//       return { ...r, appBundles: bundles };
//     });
//   };

//   const setMappingField = (intentIdx, mapIdx, field, value) => {
//     setRow((r) => {
//       const bundles = [...r.appBundles];
//       const item = bundles[intentIdx];
//       const maps = [...item.mappings];
//       maps[mapIdx] = { ...maps[mapIdx], [field]: value };
//       item.mappings = maps;
//       return { ...r, appBundles: bundles };
//     });
//   };

//   // Disable app used within the same intent
//   const appDisabledInIntent = (intentIdx, appId) => {
//     const bundle = row.appBundles[intentIdx];
//     return bundle.mappings.some((m) => m.appId === appId);
//   };

//   // --- Validation ---
//   const baseSelectorsComplete = row.accountId && row.orgId && row.spaceId;
//   const industrySelected = !!row.domain;

//   const intentsValid = useMemo(() => {
//     if (!row.appBundles.length) return false;
//     const usedIntents = new Set();
//     for (const b of row.appBundles) {
//       if (!b.intent || usedIntents.has(b.intent)) return false;
//       usedIntents.add(b.intent);
//       if (!Array.isArray(b.mappings) || !b.mappings.length) return false;
//       const seenApps = new Set();
//       for (const m of b.mappings) {
//         if (!m.purpose || !m.appId) return false;
//         if (seenApps.has(m.appId)) return false; // unique per intent
//         seenApps.add(m.appId);
//       }
//     }
//     return true;
//   }, [row.appBundles]);

//   const allValid = baseSelectorsComplete && industrySelected && intentsValid;

//   // --- Payload ---
//   const buildPayload = () => ({
//     podio: [{
//       workspace: String(row.spaceId),
//       accountId: String(row.accountId),
//       organization: String(row.orgId),
//       domain: String(row.domain),
//       app: row.appBundles.map(b => ({
//         intent: String(b.intent),
//         mappings: b.mappings.map(m => ({
//           purpose: String(m.purpose),
//           appId: String(m.appId),
//         })),
//       }))
//     }]
//   });

//   // --- Save/Reset ---
//   const onSave = async () => {
//     if (!baseSelectorsComplete) return toast.error("Complete Account, Organization, and Workspace.");
//     if (!industrySelected) return toast.error("Select an Industry.");
//     if (!intentsValid) return toast.error("Fix intent/mapping validation errors.");

//     try {
//       setSaving(true);
//       const payload = buildPayload();
//       if (configId) {
//         const updated = await updateChatbotConfig(payload);
//         if (updated?._id) toast.success('Chatbot configuration updated!');
//         else toast.success('Configuration saved.');
//       } else {
//         const created = await createChatbotConfig(payload);
//         if (created?._id) {
//           setConfigId(created._id);
//           toast.success('Chatbot configuration created!');
//         } else toast.success('Configuration saved.');
//       }
//     } catch (e) {
//       toast.error('Failed to save chatbot configuration.');
//     } finally {
//       setSaving(false);
//     }
//   };

//   const onReset = async () => {
//     try {
//       setResetting(true);
//       const res = await deleteChatbotConfig();
//       if (res?.success) {
//         setConfigId(null);
//         setRow({ ...EMPTY_ROW });
//         toast.success('Chatbot configuration reset.');
//       } else {
//         toast.error('Failed to reset chatbot configuration.');
//       }
//     } finally {
//       setResetting(false);
//     }
//   };

//   return (
//     <>
//       <Row>
//         <Col xs="12">
//           <WidgetsSectionTitle
//             className="my-2"
//             title="Attach Podio apps to Chatbot (by Intent)"
//             subtitle="Select Account → Organization → Workspace → Industry → map Intents to Apps with a Purpose."
//             icon="settings"
//           />
//         </Col>

//         <Col xs="12">
//           <Card className="mb-3">
//             <CardBody>
//               {/* Account */}
//               <FormGroup>
//                 <Label>Account</Label>
//                 <Input
//                   type="select"
//                   value={row.accountId}
//                   onChange={(e) => onAccountChange(e.target.value)}
//                 >
//                   <option value="">Select an account</option>
//                   {accountOptions.map((acc) => (
//                     <option key={acc._id} value={acc._id}>
//                       {acc.email} {acc.status ? `(${acc.status})` : ''}
//                     </option>
//                   ))}
//                 </Input>
//               </FormGroup>

//               {/* Organization */}
//               <FormGroup>
//                 <Label>Organization</Label>
//                 <Input
//                   type="select"
//                   value={row.orgId}
//                   onChange={(e) => onOrgChange(row.accountId, e.target.value)}
//                   disabled={!row.accountId}
//                 >
//                   <option value="">Select an organization</option>
//                   {(row.accountId ? getOrgs(row.accountId) : []).map((org) => (
//                     <option key={org.org_id} value={org.org_id}>
//                       {org.name}
//                     </option>
//                   ))}
//                 </Input>
//               </FormGroup>

//               {/* Workspace */}
//               <FormGroup>
//                 <Label>Workspace</Label>
//                 <Input
//                   type="select"
//                   value={row.spaceId}
//                   onChange={(e) => onSpaceChange(row.accountId, e.target.value)}
//                   disabled={!row.orgId}
//                 >
//                   <option value="">Select a workspace</option>
//                   {(row.orgId ? getSpaces(row.orgId) : []).map((space) => (
//                     <option key={space.space_id} value={space.space_id}>
//                       {space.name}
//                     </option>
//                   ))}
//                 </Input>
//               </FormGroup>

//               {/* Industry / Domain */}
//               <FormGroup>
//                 <Label>Industry</Label>
//                 <Input
//                   type="select"
//                   value={row.domain}
//                   onChange={(e) => onIndustryChange(e.target.value)}
//                   disabled={!row.spaceId}
//                 >
//                   <option value="">Select an industry</option>
//                   {INDUSTRY_OPTIONS.map((opt) => (
//                     <option key={opt.domain} value={opt.domain}>{opt.label}</option>
//                   ))}
//                 </Input>
//                 {row.domain && (intentsLoading ? (
//                   <div className="text-muted mt-1" style={{ fontSize: 12 }}>Loading intents…</div>
//                 ) : availableIntents.length === 0 ? (
//                   <div className="text-danger mt-1" style={{ fontSize: 12 }}>
//                     No intents found for {row.domain}.
//                   </div>
//                 ) : null)}
//               </FormGroup>

//               {/* Intent Blocks */}
//               {!!row.domain && row.appBundles.map((bundle, bIdx) => {
//                 // Intents dropdown options: disable ones already used elsewhere
//                 return (
//                   <Card key={`intent-${bIdx}`} className="mb-3 border">
//                     <CardBody>
//                       <div className="d-flex justify-content-between align-items-center">
//                         <h6 className="mb-2">Intent #{bIdx + 1}</h6>
//                         <Button color="link" onClick={() => removeIntent(bIdx)}>
//                           Remove Intent
//                         </Button>
//                       </div>

//                       <FormGroup>
//                         <Label>Intent</Label>
//                      <Input
//   type="select"
//   value={extractIntentKey(bundle.intent)}   // <-- use key for the control
//   onChange={(e) => setIntentKey(bIdx, e.target.value)}
// >
//   <option value="">Select an intent</option>
//   {availableIntents.map((it) => {
//     const currentKey = extractIntentKey(bundle.intent);
//     const disabled = intentAlreadySelected(it.key, currentKey);
//     const caption = `${it.key.replace(/_/g,' ') }: ${it.description}`;
//     return (
//       <option key={it.key} value={it.key} disabled={disabled}>
//         {caption}
//       </option>
//     );
//   })}
// </Input>

//                       </FormGroup>

//                       {/* Mappings for this intent */}
//                       {bundle.mappings.map((m, mIdx) => (
//                         <Row key={`map-${bIdx}-${mIdx}`} className="align-items-end">
//                           <Col md="7">
//                             <FormGroup>
//                               <Label>Purpose</Label>
//                               <Input
//                                 type="textarea"
//                                 value={m.purpose}
//                                 onChange={(e) => setMappingField(bIdx, mIdx, 'purpose', e.target.value)}
//                                 placeholder="Describe why this app is used for this intent"
//                               />
//                             </FormGroup>
//                           </Col>
//                           <Col md="4">
//                             <FormGroup>
//                               <Label>App</Label>
//                               <Input
//                                 type="select"
//                                 value={m.appId}
//                                 onChange={(e) => setMappingField(bIdx, mIdx, 'appId', e.target.value)}
//                                 disabled={!row.spaceId}
//                               >
//                                 <option value="">Select an app</option>
//                                 {appOptions.map((app) => {
//                                   const id = String(app.app_id);
//                                   const disabled = appDisabledInIntent(bIdx, id) && id !== m.appId;
//                                   const label = app.config?.name || app.config?.item_name || app.url_label || app.app_id;
//                                   return (
//                                     <option key={id} value={id} disabled={disabled}>
//                                       {label}
//                                     </option>
//                                   );
//                                 })}
//                               </Input>
//                             </FormGroup>
//                           </Col>
//                           <Col md="1" className="mb-3">
//                             <Button color="link" onClick={() => removeMapping(bIdx, mIdx)}>
//                               Remove
//                             </Button>
//                           </Col>
//                         </Row>
//                       ))}

//                       <Button
//                         color="secondary"
//                         onClick={() => addMapping(bIdx)}
//                         disabled={!bundle.intent || appOptions.length === 0}
//                       >
//                         + Add App
//                       </Button>
//                     </CardBody>
//                   </Card>
//                 );
//               })}

//               {/* Add intent */}
//               {!!row.domain && (
//                 <Button
//                   color="info"
//                   onClick={addIntent}
//                   disabled={availableIntents.length === 0 || selectedIntentKeys.size >= availableIntents.length}
//                 >
//                   + Add Intent
//                 </Button>
//               )}

//               {/* Validation hint */}
//               {!allValid && (
//                 <div className="text-muted mt-2" style={{ fontSize: 12 }}>
//                   Complete all dropdowns and ensure each intent has unique apps with a purpose.
//                 </div>
//               )}
//             </CardBody>
//           </Card>
//         </Col>

//         {/* Actions */}
//         <Col xs="12" className="mb-3 d-flex gap-2">
//           <Button
//             color="success"
//             onClick={onSave}
//             disabled={!allValid || saving || podioLoading}
//           >
//             {configId ? 'Save Configuration' : 'Create Configuration'}
//           </Button>

//           <Button color="danger" onClick={onReset} disabled={!configId || resetting}>
//             Reset Configurations
//           </Button>
//         </Col>
//       </Row>
//     </>
//   );
// };

// const mapStateToProps = (state) => ({
//   // podio lookup
//   podioLoading: state.podioLookup?.loading,
//   accounts: state.podioLookup?.accounts || [],
//   orgsByAccount: state.podioLookup?.orgsByAccount || {},
//   spacesByOrg: state.podioLookup?.spacesByOrg || {},
//   appsBySpace: state.podioLookup?.appsBySpace || {},

//   // intents
//   intentsLoading: state.intents?.loading,
//   intentsByDomain: state.intents?.byDomain || {},
// });

// export default connect(mapStateToProps, {
//   fetchPodioAccounts,
//   fetchPodioOrgs,
//   fetchPodioSpaces,
//   fetchPodioApps,
//   fetchIntentsByDomain,
//   fetchChatbotConfig,
//   createChatbotConfig,
//   updateChatbotConfig,
//   deleteChatbotConfig,
// })(ChatbotConfig);






















// // OLD WITH SINGLE APP
// // import React, { useEffect, useMemo, useState } from 'react';
// // import { Row, Col, Card, CardBody, Button, Input, Label, FormGroup } from 'reactstrap';
// // import { connect } from 'react-redux';
// // import WidgetsSectionTitle from '../../../WidgetsSectionTitle/WidgetSectionTitle';
// // import { toast } from 'react-toastify';

// // // Chatbot config CRUD
// // import {
// //   fetchChatbotConfig,
// //   createChatbotConfig,
// //   updateChatbotConfig,
// //   deleteChatbotConfig,
// // } from '../../../Redux/Actions/AI/aichatbot';

// // // Podio lookups (accounts/token/orgs/spaces/apps)
// // import {
// //   fetchPodioAccounts,
// //   fetchPodioOrgs,
// //   fetchPodioSpaces,
// //   fetchPodioApps,
// // } from '../../../Redux/Actions/AI/podiolookup';

// // const EMPTY_ROW = {
// //   accountId: '',
// //   orgId: '',
// //   spaceId: '',
// //   appId: '',
// // };

// // const ChatbotConfig = ({
// //   // lookup state
// //   podioLoading,
// //   accounts,
// //   orgsByAccount,
// //   spacesByOrg,
// //   appsBySpace,

// //   // actions
// //   fetchPodioAccounts,
// //   fetchPodioOrgs,
// //   fetchPodioSpaces,
// //   fetchPodioApps,

// //   // chatbot config
// //   fetchChatbotConfig,
// //   createChatbotConfig,
// //   updateChatbotConfig,
// //   deleteChatbotConfig,
// // }) => {
// //   // --- Single row state ---
// //   const [row, setRow] = useState({ ...EMPTY_ROW });
// //   const [configId, setConfigId] = useState(null);
// //   const [saving, setSaving] = useState(false);
// //   const [resetting, setResetting] = useState(false);

// //   // --- Helpers to get cached lists ---
// //   const getOrgs = (accountId) => orgsByAccount?.[accountId] || [];
// //   const getSpaces = (orgId) => spacesByOrg?.[orgId] || [];
// //   const getApps = (spaceId) => appsBySpace?.[spaceId] || [];

// //   // --- Init: load accounts & existing config ---
// //   useEffect(() => {
// //     fetchPodioAccounts();
// //     (async () => {
// //       const res = await fetchChatbotConfig();
// //       if (res?._id && Array.isArray(res.podio) && res.podio.length > 0) {
// //         const first = res.podio[0];
// //         setConfigId(res._id);
// //         const initialRow = {
// //           accountId: first.accountId || '',
// //           orgId: first.organization || '',
// //           spaceId: first.workspace || '',
// //           appId: first.app || '',
// //         };
// //         setRow(initialRow);

// //         // Preload dependent dropdown data for the hydrated selection
// //         if (first.accountId) await fetchPodioOrgs(first.accountId);
// //         if (first.accountId && first.organization) await fetchPodioSpaces(first.accountId, first.organization);
// //         if (first.accountId && first.workspace) await fetchPodioApps(first.accountId, first.workspace);
// //       }
// //     })();
// //     // eslint-disable-next-line react-hooks/exhaustive-deps
// //   }, []);

// //   // --- Dropdown change handlers with cascade clears ---
// //   const onAccountChange = async (newAccountId) => {
// //     setRow({ accountId: newAccountId, orgId: '', spaceId: '', appId: '' });
// //     if (newAccountId) await fetchPodioOrgs(newAccountId);
// //   };

// //   const onOrgChange = async (accountId, newOrgId) => {
// //     setRow((r) => ({ ...r, orgId: newOrgId, spaceId: '', appId: '' }));
// //     if (accountId && newOrgId) await fetchPodioSpaces(accountId, newOrgId);
// //   };

// //   const onSpaceChange = async (accountId, newSpaceId) => {
// //     setRow((r) => ({ ...r, spaceId: newSpaceId, appId: '' }));
// //     if (accountId && newSpaceId) await fetchPodioApps(accountId, newSpaceId);
// //   };

// //   const onAppChange = (newAppId) => {
// //     setRow((r) => ({ ...r, appId: newAppId }));
// //   };

// //   // --- Validation ---
// //   const rowIsComplete = (r) => r.accountId && r.orgId && r.spaceId && r.appId;
// //   const allRowsValid = useMemo(() => rowIsComplete(row || {}), [row]);

// //   // --- Build payload for API ---
// //   const buildPayload = () => ({
// //     podio: rowIsComplete(row)
// //       ? [
// //           {
// //             workspace: String(row.spaceId),
// //             app: String(row.appId),
// //             accountId: String(row.accountId),
// //             organization: String(row.orgId),
// //           },
// //         ]
// //       : [],
// //   });

// //   // --- Save (create/update) ---
// //   const onSave = async () => {
// //     if (!allRowsValid) {
// //       toast.error('Please complete all dropdowns before saving.');
// //       return;
// //     }
// //     try {
// //       setSaving(true);
// //       const payload = buildPayload();
// //       if (configId) {
// //         const updated = await updateChatbotConfig(payload);
// //         if (updated?._id) {
// //           toast.success('Chatbot configuration updated!');
// //         } else {
// //           toast.success('Configuration saved.');
// //         }
// //       } else {
// //         const created = await createChatbotConfig(payload);
// //         if (created?._id) {
// //           setConfigId(created._id);
// //           toast.success('Chatbot configuration created!');
// //         } else {
// //           toast.success('Configuration saved.');
// //         }
// //       }
// //     } catch (e) {
// //       toast.error('Failed to save chatbot configuration.');
// //     } finally {
// //       setSaving(false);
// //     }
// //   };

// //   // --- Reset (delete config & clear UI) ---
// //   const onReset = async () => {
// //     try {
// //       setResetting(true);
// //       const res = await deleteChatbotConfig();
// //       if (res?.success) {
// //         setConfigId(null);
// //         setRow({ ...EMPTY_ROW });
// //         toast.success('Chatbot configuration reset.');
// //       } else {
// //         toast.error('Failed to reset chatbot configuration.');
// //       }
// //     } finally {
// //       setResetting(false);
// //     }
// //   };

// //   // --- Render helpers ---
// //   const accountOptions = accounts;
// //   const orgOptions = row.accountId ? getOrgs(row.accountId) : [];
// //   const spaceOptions = row.orgId ? getSpaces(row.orgId) : [];
// //   const appOptions = row.spaceId ? getApps(row.spaceId) : [];

// //   return (
// //     <>
// //       <Row>
// //         <Col xs="12">
// //           <WidgetsSectionTitle
// //             className="my-2"
// //             title="Attach a Podio app to Chatbot"
// //             subtitle="Map your Podio Account → Organization → Workspace → App"
// //             icon="settings"
// //           />
// //         </Col>

// //         {/* Single App Card */}
// //         <Col xs="12">
// //           <Card className="mb-3">
// //             <CardBody>
// //               <div className="d-flex justify-content-between align-items-center mb-2">
// //                 <h5 className="mb-0">Selected App</h5>
// //               </div>

// //               {/* Account */}
// //               <FormGroup>
// //                 <Label>Account</Label>
// //                 <Input
// //                   type="select"
// //                   value={row.accountId}
// //                   onChange={(e) => onAccountChange(e.target.value)}
// //                 >
// //                   <option value="">Select an account</option>
// //                   {accountOptions.map((acc) => (
// //                     <option key={acc._id} value={acc._id}>
// //                       {acc.email} {acc.status ? `(${acc.status})` : ''}
// //                     </option>
// //                   ))}
// //                 </Input>
// //               </FormGroup>

// //               {/* Organization */}
// //               <FormGroup>
// //                 <Label>Organization</Label>
// //                 <Input
// //                   type="select"
// //                   value={row.orgId}
// //                   onChange={(e) => onOrgChange(row.accountId, e.target.value)}
// //                   disabled={!row.accountId}
// //                 >
// //                   <option value="">Select an organization</option>
// //                   {orgOptions.map((org) => (
// //                     <option key={org.org_id} value={org.org_id}>
// //                       {org.name}
// //                     </option>
// //                   ))}
// //                 </Input>
// //               </FormGroup>

// //               {/* Workspace */}
// //               <FormGroup>
// //                 <Label>Workspace</Label>
// //                 <Input
// //                   type="select"
// //                   value={row.spaceId}
// //                   onChange={(e) => onSpaceChange(row.accountId, e.target.value)}
// //                   disabled={!row.orgId}
// //                 >
// //                   <option value="">Select a workspace</option>
// //                   {spaceOptions.map((space) => (
// //                     <option key={space.space_id} value={space.space_id}>
// //                       {space.name}
// //                     </option>
// //                   ))}
// //                 </Input>
// //               </FormGroup>

// //               {/* App */}
// //               <FormGroup>
// //                 <Label>App</Label>
// //                 <Input
// //                   type="select"
// //                   value={row.appId}
// //                   onChange={(e) => onAppChange(e.target.value)}
// //                   disabled={!row.spaceId}
// //                 >
// //                   <option value="">Select an app</option>
// //                   {appOptions.map((app) => (
// //                     <option key={app.app_id} value={app.app_id}>
// //                       {app.config?.name || app.config?.item_name || app.url_label || app.app_id}
// //                     </option>
// //                   ))}
// //                 </Input>
// //               </FormGroup>

// //               {/* Validation hint */}
// //               {!rowIsComplete(row) && (
// //                 <div className="text-muted" style={{ fontSize: 12 }}>
// //                   Select all dropdowns to complete this mapping.
// //                 </div>
// //               )}
// //             </CardBody>
// //           </Card>
// //         </Col>

// //         {/* Actions */}
// //         <Col xs="12" className="mb-3 d-flex gap-2">
// //           <Button
// //             color="success"
// //             onClick={onSave}
// //             disabled={!allRowsValid || saving || podioLoading}
// //           >
// //             {configId ? 'Save Configuration' : 'Create Configuration'}
// //           </Button>

// //           <Button color="danger" onClick={onReset} disabled={!configId || resetting}>
// //             Reset Configurations
// //           </Button>
// //         </Col>
// //       </Row>
// //     </>
// //   );
// // };

// // const mapStateToProps = (state) => ({
// //   // lookup state
// //   podioLoading: state.podioLookup?.loading,
// //   accounts: state.podioLookup?.accounts || [],
// //   orgsByAccount: state.podioLookup?.orgsByAccount || {},
// //   spacesByOrg: state.podioLookup?.spacesByOrg || {},
// //   appsBySpace: state.podioLookup?.appsBySpace || {},
// // });

// // export default connect(mapStateToProps, {
// //   fetchPodioAccounts,
// //   fetchPodioOrgs,
// //   fetchPodioSpaces,
// //   fetchPodioApps,
// //   fetchChatbotConfig,
// //   createChatbotConfig,
// //   updateChatbotConfig,
// //   deleteChatbotConfig,
// // })(ChatbotConfig);

